import { useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useFleetPermissions } from "@/hooks/use-fleet-permissions";
import { Badge } from "@/components/ui/badge";
import { Plus, CircleDashed, Package, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { TireHistorySheet } from "@/components/fleet/tires/TireHistorySheet";
import { useTires, useCreateTire, useUpdateTire, useDeleteTire } from "@/hooks/use-tires";
import { useTireInstallation } from "@/hooks/use-tire-events";
import { TireCard } from "@/components/fleet/tires/TireCard";
import { TireFormSheet } from "@/components/fleet/tires/TireFormSheet";
import { TireFilters } from "@/components/fleet/tires/TireFilters";
import { TireEventTimeline } from "@/components/fleet/tires/TireEventTimeline";
import { TireInstallationSheet } from "@/components/fleet/tires/TireInstallationSheet";

export default function FleetTires() {
  const { activeCompany } = useAuth();
  const companyId = activeCompany?.id;
  const { canCreate, canEdit, isViewer } = useFleetPermissions();

  const [filters, setFilters] = useState({ status: 'all', vehicleId: undefined, search: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isInstallOpen, setIsInstallOpen] = useState(false);
  const [selectedTire, setSelectedTire] = useState<any>(null);

  const { data: tires = [], isLoading } = useTires(companyId, filters);
  const createTire = useCreateTire(companyId);
  const updateTire = useUpdateTire(companyId);
  const deleteTire = useDeleteTire(companyId);
  const installTire = useTireInstallation(companyId);

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveTire = (data: any) => {
    if (selectedTire) {
      updateTire.mutate({ id: selectedTire.id, ...data }, {
        onSuccess: () => {
          setIsModalOpen(false);
          setSelectedTire(null);
        }
      });
    } else {
      createTire.mutate(data, {
        onSuccess: () => {
          setIsModalOpen(false);
          setSelectedTire(null);
        }
      });
    }
  };

  const handleInstall = (data: any) => {
    installTire.mutate(data);
  };

  const stats = {
    total: tires.length,
    installed: tires.filter(t => t.status === 'installed').length,
    inStock: tires.filter(t => t.status === 'stock').length,
    inRepair: tires.filter(t => t.status === 'repair').length
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Gestão de Pneus</h1>
          <p className="text-muted-foreground">
            Controle completo de pneus da frota
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {isViewer && (
            <Badge variant="secondary">
              👁️ Modo Visualização
            </Badge>
          )}
          <Button disabled={!canCreate} onClick={() => {
            setSelectedTire(null);
            setIsModalOpen(true);
          }}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Pneu
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-full bg-primary/10 p-3">
              <CircleDashed className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-full bg-green-500/10 p-3">
              <CircleDashed className="h-6 w-6 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Instalados</p>
              <p className="text-2xl font-bold">{stats.installed}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-full bg-blue-500/10 p-3">
              <Package className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Estoque</p>
              <p className="text-2xl font-bold">{stats.inStock}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-full bg-orange-500/10 p-3">
              <Wrench className="h-6 w-6 text-orange-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Reparo</p>
              <p className="text-2xl font-bold">{stats.inRepair}</p>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-4">
        <TireFilters 
          companyId={companyId}
          filters={filters}
          onFilterChange={handleFilterChange}
        />
      </Card>

      {isLoading ? (
        <div className="text-center p-12">Carregando pneus...</div>
      ) : tires.length === 0 ? (
        <Card className="p-12 text-center">
          <CircleDashed className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">Nenhum pneu cadastrado</h3>
          <p className="text-muted-foreground mb-4">
            Comece cadastrando o primeiro pneu da sua frota
          </p>
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Cadastrar Primeiro Pneu
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tires.map((tire) => (
            <TireCard
              key={tire.id}
              tire={tire}
              onEdit={canEdit ? () => {
                setSelectedTire(tire);
                setIsModalOpen(true);
              } : undefined}
              onHistory={() => {
                setSelectedTire(tire);
                setIsHistoryOpen(true);
              }}
              onInstall={canEdit ? () => {
                setSelectedTire(tire);
                setIsInstallOpen(true);
              } : undefined}
            />
          ))}
        </div>
      )}

      <TireFormSheet
        open={isModalOpen}
        onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open) setSelectedTire(null);
        }}
        onSave={handleSaveTire}
        tire={selectedTire}
        companyId={companyId}
      />

      <TireHistorySheet
        open={isHistoryOpen}
        onOpenChange={setIsHistoryOpen}
        tire={selectedTire}
      />

      <TireInstallationSheet
        open={isInstallOpen}
        onOpenChange={(open) => {
          setIsInstallOpen(open);
          if (!open) setSelectedTire(null);
        }}
        onInstall={handleInstall}
        tire={selectedTire}
        companyId={companyId}
      />
    </div>
  );
}
